'use client';

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  MapPin,
  Globe,
  Mail,
  Phone,
  Edit2,
  UserPlus,
  UserCheck,
  Clock,
  ChevronDown,
  UserMinus,
  X,
  Share2,
  FileUser,
  Clipboard,
  ArrowLeft,
  PhoneCall,
  Loader2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { canViewerSeeField, getFieldVisibility } from '@/components/forms/date-visibility';
import {
  getProfileUrl,
  type PublicUser,
  type PublicBusiness,
  type UserBusinessProfile,
  type PublicUserWithCommunities,
} from '@/types/profile';

import { displayBusinessAddressFields, hasBusinessAddressForDisplay } from '@/lib/format-address';
import { stripHtmlToPlainText } from '@/lib/strip-html';
import { formatBusinessCategoryLabel } from '@/lib/business-category';
import { formatDisplayPhone, phoneTelHref } from '@/lib/phone-utils';
import { canShareVCard, shareVCard } from '@/lib/share-vcard';
import useSWR, { mutate as globalMutate } from 'swr';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { useUser } from '@/hooks/use-user';
import { SocialLinksCircleEditor } from '@/components/social/social-links-circle-editor';
import {
  BusinessBasicInfoSection,
  type BusinessBasicInfoSaved,
} from '@/components/profile/business-basic-info-section';
import { useCallOptional } from '@/providers/call-context';

const fetcher = (url: string) => axiosInstance.get(url).then((res) => res.data);

interface ProfileHeaderProps {
  type: 'user' | 'business' | 'user-business';
  user?: PublicUser;
  business?: PublicBusiness;
  userBusiness?: UserBusinessProfile;
  isEditable?: boolean;
  onEdit?: () => void;
  /** When true, business header card expands to Basic Information editor on the same page. */
  isEditingBasic?: boolean;
  onBasicCancel?: () => void;
  onBasicSaved?: (updated: BusinessBasicInfoSaved) => void;
}

type ShareMode = 'options' | 'text' | 'vcard';

const escapeVCardValue = (value: string) =>
  value
    .replaceAll('\\', '\\\\')
    .replaceAll(';', '\\;')
    .replaceAll(',', '\\,')
    .replaceAll('\n', '\\n');

function InfoPill({
  icon,
  href,
  children,
  className,
}: {
  icon: React.ReactNode;
  href?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const base =
    'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full border text-[11px] font-semibold transition-all shrink-0 ' +
    'bg-background/60 border-border/60 text-foreground/80 hover:border-primary/40 hover:text-primary hover:bg-primary/5';
  if (href) {
    return (
      <a href={href} className={cn(base, className)}>
        <span className="text-primary/70 shrink-0">{icon}</span>
        {children}
      </a>
    );
  }
  return (
    <span className={cn(base, className)}>
      <span className="text-primary/70 shrink-0">{icon}</span>
      {children}
    </span>
  );
}

export function ProfileHeader({
  type,
  user,
  business,
  userBusiness,
  isEditable,
  onEdit,
  isEditingBasic = false,
  onBasicCancel,
  onBasicSaved,
}: ProfileHeaderProps) {
  const { user: currentUser } = useUser();
  const callApi = useCallOptional();
  const [isStartingCall, setIsStartingCall] = useState(false);
  const isOwnProfile = Boolean(currentUser?.id && user?.id && currentUser.id === user.id);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [shareMode, setShareMode] = useState<ShareMode>('options');
  const navigate = useNavigate();
  const { communityId } = useParams();
  const [isMutualOpen, setIsMutualOpen] = useState(false);
  const initialSocialLinks =
    type === 'business'
      ? ((business?.socialLinks as Record<string, string> | null | undefined) ?? {})
      : ((user?.socialLinks as Record<string, string> | null | undefined) ??
        (userBusiness?.user?.socialLinks as Record<string, string> | null | undefined) ??
        {});

  const [socialLinks, setSocialLinks] = useState<Record<string, string>>(initialSocialLinks);
  const [isSavingSocial, setIsSavingSocial] = useState(false);

  useEffect(() => {
    if (type === 'business') {
      setSocialLinks((business?.socialLinks as Record<string, string> | null | undefined) ?? {});
      return;
    }
    setSocialLinks(
      (user?.socialLinks as Record<string, string> | null | undefined) ??
        (userBusiness?.user?.socialLinks as Record<string, string> | null | undefined) ??
        {}
    );
  }, [type, business?.socialLinks, user?.socialLinks, userBusiness?.user?.socialLinks]);

  const handleSocialLinksChange = async (val: Record<string, string>) => {
    setSocialLinks(val);
    setIsSavingSocial(true);
    try {
      if (type === 'business' && business?.id) {
        const addr =
          business.addressJson && typeof business.addressJson === 'object'
            ? (business.addressJson as Record<string, string>)
            : undefined;
        await axiosInstance.patch('/api/user/business', {
          id: business.id,
          name: business.name,
          phone: business.phone,
          email: business.email,
          addressJson: {
            addressLine1: addr?.addressLine1 || business.address || 'Not provided',
            addressLine2: addr?.addressLine2 || '',
            landmark: addr?.landmark || '',
            taluka: addr?.taluka || '',
            district: addr?.district || '',
            state: addr?.state || 'Not provided',
            country: addr?.country || 'Not provided',
            pinCode: addr?.pinCode && addr.pinCode.length >= 4 ? addr.pinCode : '000000',
            googleMapLink: addr?.googleMapLink || '',
          },
          socialLinks: val,
        });
        await globalMutate(`/api/public/business/${business.id}`);
        await globalMutate('/api/user');
      } else {
        await axiosInstance.patch('/api/user', { socialLinks: val });
        await globalMutate('/api/user');
      }
    } catch {
      toast.error('Failed to save social links');
    } finally {
      setIsSavingSocial(false);
    }
  };

  const getProfileRedirectUrl = (targetUserId?: string, targetBusinessId?: string) => {
    if (communityId) {
      if (targetUserId && targetBusinessId) {
        return `/${communityId}/ub/${targetUserId}/${targetBusinessId}`;
      }
      if (targetBusinessId) {
        return `/${communityId}/b/${targetBusinessId}`;
      }
      if (targetUserId) {
        return `/${communityId}/u/${targetUserId}`;
      }
    }
    return getProfileUrl({ userId: targetUserId, businessId: targetBusinessId });
  };

  const displayName = userBusiness
    ? `${userBusiness.user.firstName} ${userBusiness.user.lastName}`
    : user
      ? `${user.firstName} ${user.lastName}`
      : business?.name || 'Unknown';

  const subName = userBusiness
    ? business?.name
    : type === 'user'
      ? user?.email
      : formatBusinessCategoryLabel(
          business?.category,
          business?.subCategory,
          business?.selfDefinedCategory
        );

  const image = userBusiness?.user.image || user?.image || business?.logo;
  const initials = displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const contactName = [user?.firstName, user?.middleName, user?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  const contactNumber = user?.phone?.trim() || '';
  const contactEmail = user?.email?.trim() || '';
  const contactText = [
    `Name: ${contactName || displayName}`,
    `Number: ${contactNumber}`,
    `Email: ${contactEmail}`,
  ].join('\n');
  const vCard = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${escapeVCardValue(contactName || displayName)}`,
    `N:${escapeVCardValue(user?.lastName || '')};${escapeVCardValue(user?.firstName || '')};${escapeVCardValue(user?.middleName || '')};;`,
    `TEL;TYPE=CELL:${escapeVCardValue(contactNumber)}`,
    `EMAIL;TYPE=INTERNET:${escapeVCardValue(contactEmail)}`,
    'END:VCARD',
  ].join('\r\n');

  const handleShareText = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: `${contactName || displayName} on Milora`,
          text: contactText,
        });
        toast.success('Contact shared');
        return;
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
    }

    try {
      await navigator.clipboard.writeText(contactText);
      toast.success('Contact details copied');
    } catch {
      toast.error('Unable to share contact details');
    }
  };

  const showShareVCard = canShareVCard();

  const handleShareVCard = async () => {
    const result = await shareVCard({
      vCard,
      filename: (contactName || 'milora-contact').replace(/\s+/g, '-'),
      title: `${contactName || displayName} on Milora`,
    });

    if (result === 'cancelled') return;
    if (result === 'shared') {
      toast.success('Contact card shared');
      return;
    }
    toast.error('Unable to share contact card');
  };

  interface FriendData {
    sent?: string[];
    friends?: string[];
    requests?: { id: string; senderId: string; direction: string }[];
  }

  const { data: friendData, mutate: mutateFriends } = useSWR<FriendData>(
    type === 'user' && !isEditable ? '/api/user/friends/request' : null,
    fetcher
  );

  const mutualFriends = (user as PublicUserWithCommunities)?.mutualFriends || [];

  const handleAddFriend = async (e: React.MouseEvent, receiverId: string) => {
    e.stopPropagation();
    try {
      mutateFriends((current) => {
        if (!current) return current;
        return {
          ...current,
          sent: [...(current.sent || []), receiverId],
        };
      }, false);

      toast.promise(
        axiosInstance.post('/api/user/friends/request', {
          receiverId,
          ...(communityId ? { communityId } : {}),
        }),
        {
          loading: 'Sending friend request...',
          success: () => {
            mutateFriends();
            return 'Friend request sent!';
          },
          error: (err: unknown) => {
            mutateFriends();
            const axiosErr = err as { response?: { data?: { error?: string } } };
            return axiosErr.response?.data?.error || 'Failed to send friend request';
          },
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleAcceptFriend = async (e: React.MouseEvent, requestId: string) => {
    e.stopPropagation();
    try {
      toast.promise(
        axiosInstance.post(`/api/user/friends/request/${requestId}/respond`, { action: 'ACCEPT' }),
        {
          loading: 'Accepting friend request...',
          success: () => {
            mutateFriends();
            return 'Friend request accepted!';
          },
          error: (err: unknown) => {
            mutateFriends();
            const axiosErr = err as { response?: { data?: { error?: string } } };
            return axiosErr.response?.data?.error || 'Failed to accept request';
          },
        }
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleRemoveFriend = async (e: React.MouseEvent, friendId: string) => {
    e.stopPropagation();
    try {
      mutateFriends((current) => {
        if (!current) return current;
        return {
          ...current,
          friends: (current.friends || []).filter((id) => id !== friendId),
        };
      }, false);

      toast.promise(axiosInstance.delete('/api/user/friends/request', { data: { friendId } }), {
        loading: 'Removing friend...',
        success: () => {
          mutateFriends();
          return 'Removed friend successfully';
        },
        error: (err: unknown) => {
          mutateFriends();
          const axiosErr = err as { response?: { data?: { error?: string } } };
          return axiosErr.response?.data?.error || 'Failed to remove friend';
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  const handleCancelRequest = async (e: React.MouseEvent, friendId: string) => {
    e.stopPropagation();
    try {
      mutateFriends((current) => {
        if (!current) return current;
        return {
          ...current,
          sent: (current.sent || []).filter((id) => id !== friendId),
        };
      }, false);

      toast.promise(axiosInstance.delete('/api/user/friends/request', { data: { friendId } }), {
        loading: 'Cancelling request...',
        success: () => {
          mutateFriends();
          return 'Friend request cancelled';
        },
        error: (err: unknown) => {
          mutateFriends();
          const axiosErr = err as { response?: { data?: { error?: string } } };
          return axiosErr.response?.data?.error || 'Failed to cancel friend request';
        },
      });
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className="relative mb-4">
      {/* ── CARD ── */}
      <div className="max-w-5xl mx-auto px-3 md:px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.38, ease: [0.22, 1, 0.36, 1] }}
          className="glass-card relative overflow-hidden rounded-2xl md:rounded-3xl border-primary/20 bg-primary/[0.06] shadow-xl shadow-primary/[0.08] dark:bg-primary/[0.08] dark:border-primary/25"
        >
          {/* Primary glass tint */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/15 via-primary/5 to-transparent" />
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,hsl(var(--primary)/0.14),transparent_55%)]" />
          {/* Top highlight line */}
          <div className="pointer-events-none absolute top-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-primary/25 to-transparent" />

          {/* Top-right icon cluster: business edit / Share (other profiles) / Call */}
          {(type === 'business' && isEditable && onEdit && !isEditingBasic) ||
          (type === 'user' && user && !isOwnProfile && !isEditable) ? (
            <div className="absolute top-3 right-3 z-20 flex flex-col items-center gap-1.5">
              {type === 'business' && isEditable && onEdit && !isEditingBasic ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Edit basic information"
                  className="h-9 w-9 rounded-xl border border-border/30 bg-background/60 backdrop-blur-sm shadow-sm hover:bg-primary/10 hover:text-primary hover:border-primary/30 text-muted-foreground transition-all"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onEdit();
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              ) : null}
              {type === 'user' && user && !isOwnProfile && !isEditable ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Share contact"
                  className="h-9 w-9 rounded-xl bg-primary text-primary-foreground shadow-lg shadow-primary/30 hover:bg-primary/90 hover:shadow-primary/40 active:scale-[0.97] transition-all"
                  onClick={() => {
                    setShareMode('options');
                    setShareDialogOpen(true);
                  }}
                >
                  <Share2 className="h-4 w-4" />
                </Button>
              ) : null}
              {type === 'user' &&
              user &&
              !isOwnProfile &&
              currentUser?.id &&
              currentUser.isDeveloper &&
              callApi ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label="Call"
                  className="h-9 w-9 rounded-xl bg-emerald-600 text-white shadow-md shadow-emerald-500/20 hover:bg-emerald-700 hover:text-white transition-all"
                  disabled={isStartingCall || callApi.phase !== 'idle'}
                  onClick={() => {
                    setIsStartingCall(true);
                    void callApi.startCall(user.id).finally(() => setIsStartingCall(false));
                  }}
                >
                  {isStartingCall ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <PhoneCall className="h-4 w-4" />
                  )}
                </Button>
              ) : null}
            </div>
          ) : null}

          {/* ── CARD BODY ── */}
          {type === 'business' && isEditingBasic && business ? (
            <div className="relative p-4 md:p-6">
              <BusinessBasicInfoSection
                business={business}
                embedded
                onCancel={() => onBasicCancel?.()}
                onSaved={onBasicSaved}
              />
            </div>
          ) : (
            <>
              <div className="relative flex flex-col md:flex-row gap-3 md:gap-5 p-4 md:p-5 md:items-center">
                {/* Left: avatar + info */}
                <div className="flex items-center gap-3 md:gap-4 min-w-0 flex-1">
                  {/* Avatar (edit/change/remove lives in Personal & Contact Information) */}
                  <div className="relative shrink-0">
                    <div className="absolute -inset-2 rounded-full bg-primary opacity-30 blur-xl" />
                    <div className="absolute -inset-[3px] rounded-full bg-primary opacity-75" />
                    <div className="absolute -inset-[2px] rounded-full bg-card" />
                    <Avatar className="relative h-[72px] w-[72px] md:h-[88px] md:w-[88px] shadow-lg">
                      <AvatarImage src={image} className="object-cover" />
                      <AvatarFallback className="bg-primary text-primary-foreground text-2xl font-black font-display">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                  </div>

                  {/* Name + subtitle + info pills */}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-0.5">
                      <h1
                        className={cn(
                          'font-display tracking-tight text-foreground leading-none truncate',
                          type === 'business'
                            ? 'text-xl md:text-2xl font-bold'
                            : 'text-xl md:text-2xl font-black'
                        )}
                      >
                        {displayName}
                      </h1>
                      {type === 'user-business' && (
                        <Badge className="bg-primary/10 text-primary border-primary/20 font-bold uppercase tracking-wider text-[9px] px-2 py-0.5 rounded-full shrink-0">
                          {userBusiness?.role || 'FOUNDER'}
                        </Badge>
                      )}
                    </div>

                    <p
                      className={cn(
                        'text-muted-foreground truncate leading-snug mb-2.5',
                        type === 'business'
                          ? 'text-sm font-medium mt-0.5'
                          : 'text-xs font-semibold mt-0.5'
                      )}
                    >
                      {subName}
                    </p>

                    {/* Info pills row */}
                    {type !== 'business' && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {((user?.address?.city &&
                            canViewerSeeField(getFieldVisibility(user, 'city'), {
                              isOwner: Boolean(isEditable),
                            })) ||
                            (business &&
                              hasBusinessAddressForDisplay({
                                address: business.address,
                                addressJson: business.addressJson,
                              }))) && (
                            <InfoPill icon={<MapPin className="h-3 w-3" />}>
                              {(user?.address?.city &&
                              canViewerSeeField(getFieldVisibility(user, 'city'), {
                                isOwner: Boolean(isEditable),
                              })
                                ? user.address.city
                                : null) ||
                                displayBusinessAddressFields(
                                  {
                                    address: business?.address,
                                    addressJson: business?.addressJson,
                                  },
                                  true
                                )}
                            </InfoPill>
                          )}
                          {(user?.phone || business?.phone) && (
                            <InfoPill
                              icon={<Phone className="h-3 w-3" />}
                              href={phoneTelHref(user?.phone || business?.phone)}
                            >
                              {formatDisplayPhone(user?.phone || business?.phone)}
                            </InfoPill>
                          )}
                          {(user?.email || business?.email) && (
                            <InfoPill
                              icon={<Mail className="h-3 w-3" />}
                              href={`mailto:${user?.email || business?.email}`}
                            >
                              {user?.email || business?.email}
                            </InfoPill>
                          )}
                          {business?.website && (
                            <InfoPill icon={<Globe className="h-3 w-3" />} href={business.website}>
                              {new URL(business.website).hostname}
                            </InfoPill>
                          )}
                        </div>
                        {/* Social links circles row */}
                        {isEditable ? (
                          <SocialLinksCircleEditor
                            value={socialLinks}
                            onChange={handleSocialLinksChange}
                            saving={isSavingSocial}
                            size="sm"
                          />
                        ) : (
                          (() => {
                            const sl =
                              (user?.socialLinks as Record<string, string> | null | undefined) ??
                              (userBusiness?.user?.socialLinks as
                                | Record<string, string>
                                | null
                                | undefined);
                            return sl && Object.keys(sl).length > 0 ? (
                              <SocialLinksCircleEditor
                                value={sl}
                                onChange={async () => {}}
                                readOnly
                                size="sm"
                              />
                            ) : null;
                          })()
                        )}
                      </div>
                    )}

                    {/* Business type: location + website pills + social links */}
                    {type === 'business' && (
                      <div className="space-y-2">
                        <div className="flex flex-wrap items-center gap-1.5">
                          {hasBusinessAddressForDisplay({
                            address: business?.address,
                            addressJson: business?.addressJson,
                          }) && (
                            <InfoPill icon={<MapPin className="h-3 w-3" />}>
                              {displayBusinessAddressFields(
                                { address: business?.address, addressJson: business?.addressJson },
                                true
                              )}
                            </InfoPill>
                          )}
                          {business?.website && (
                            <InfoPill icon={<Globe className="h-3 w-3" />} href={business.website}>
                              {new URL(business.website).hostname}
                            </InfoPill>
                          )}
                        </div>
                        {isEditable ? (
                          <SocialLinksCircleEditor
                            value={socialLinks}
                            onChange={handleSocialLinksChange}
                            saving={isSavingSocial}
                            size="sm"
                          />
                        ) : business?.socialLinks &&
                          Object.keys(business.socialLinks).length > 0 ? (
                          <SocialLinksCircleEditor
                            value={business.socialLinks as Record<string, string>}
                            onChange={async () => {}}
                            readOnly
                            size="sm"
                          />
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>

                {/* Right: action buttons */}
                <div
                  className={cn(
                    'flex flex-row md:flex-col items-start md:items-end gap-2 shrink-0 flex-wrap',
                    type === 'user' && user && 'pr-12 md:pr-0 md:pt-12'
                  )}
                >
                  {/* Friend buttons (non-editable / other user) */}
                  {!isEditable &&
                    type === 'user' &&
                    user &&
                    !isOwnProfile &&
                    (() => {
                      const incomingReq = friendData?.requests?.find(
                        (r) => r.senderId === user.id && r.direction === 'received'
                      );
                      const isFriend = friendData?.friends?.includes(user.id);
                      const isSent = friendData?.sent?.includes(user.id);

                      if (isFriend) {
                        return (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                className="rounded-xl font-black gap-1.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white shadow-md shadow-green-500/20 h-9 text-xs uppercase tracking-wider justify-center px-4"
                              >
                                <UserCheck className="h-4 w-4" />
                                Friend
                                <ChevronDown className="h-3.5 w-3.5 opacity-80 ml-0.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-40 rounded-xl border-border bg-popover shadow-xl p-1"
                            >
                              <DropdownMenuItem
                                className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer font-bold text-xs uppercase tracking-wider rounded-lg p-2 gap-2"
                                onClick={(e) => handleRemoveFriend(e, user.id)}
                              >
                                <UserMinus className="h-3.5 w-3.5" />
                                Remove Friend
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        );
                      }

                      if (isSent) {
                        return (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                size="sm"
                                className="rounded-xl font-black gap-1.5 bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 text-white shadow-md shadow-primary/20 h-9 text-xs uppercase tracking-wider justify-center px-4"
                              >
                                <Clock className="h-4 w-4 text-primary-foreground/80 animate-pulse" />
                                Sent
                                <ChevronDown className="h-3.5 w-3.5 opacity-80 ml-0.5" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent
                              align="end"
                              className="w-40 rounded-xl border-border bg-popover shadow-xl p-1"
                            >
                              <DropdownMenuItem
                                className="text-destructive focus:bg-destructive/10 focus:text-destructive cursor-pointer font-bold text-xs uppercase tracking-wider rounded-lg p-2 gap-2"
                                onClick={(e) => handleCancelRequest(e, user.id)}
                              >
                                <X className="h-3.5 w-3.5" />
                                Undo Sent
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        );
                      }

                      if (incomingReq) {
                        return (
                          <Button
                            size="sm"
                            variant="default"
                            className="rounded-xl font-black gap-1.5 bg-gradient-to-r from-primary to-primary/70 text-white hover:opacity-90 transition-all shadow-md shadow-primary/20 h-9 text-xs uppercase tracking-wider animate-pulse justify-center px-4"
                            onClick={(e) => handleAcceptFriend(e, incomingReq.id)}
                          >
                            <UserCheck className="h-3.5 w-3.5" />
                            Accept Request
                          </Button>
                        );
                      }

                      return (
                        <Button
                          size="sm"
                          variant="default"
                          className="rounded-xl font-black gap-1.5 bg-primary text-primary-foreground hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30 transition-all active:scale-[0.97] shadow-md shadow-primary/20 h-9 text-xs uppercase tracking-wider justify-center px-4"
                          onClick={(e) => handleAddFriend(e, user.id)}
                        >
                          <UserPlus className="h-3.5 w-3.5" />
                          Add as Friend
                        </Button>
                      );
                    })()}

                  {/* Mutual friends chip */}
                  {type === 'user' && !isOwnProfile && mutualFriends.length > 0 && (
                    <>
                      <button
                        type="button"
                        onClick={() => setIsMutualOpen(true)}
                        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-xl bg-background/60 border border-border/60 shadow-sm cursor-pointer hover:bg-primary/5 hover:border-primary/30 active:scale-[0.97] transition-all"
                      >
                        <div className="flex -space-x-1.5 overflow-hidden">
                          {mutualFriends.slice(0, 3).map((friend) => {
                            const name =
                              `${friend.relative?.firstName || ''} ${friend.relative?.lastName || ''}`.trim() ||
                              'User';
                            return (
                              <Avatar
                                key={friend.id}
                                className="inline-block h-5 w-5 rounded-full ring-1 ring-card shadow-sm shrink-0"
                              >
                                <AvatarImage
                                  src={friend.relative?.image || undefined}
                                  className="object-cover"
                                />
                                <AvatarFallback className="bg-primary/10 text-primary font-bold text-[8px]">
                                  {name[0] || 'U'}
                                </AvatarFallback>
                              </Avatar>
                            );
                          })}
                          {mutualFriends.length > 3 && (
                            <div className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-muted text-[8px] font-black text-muted-foreground ring-1 ring-card shadow-sm shrink-0">
                              +{mutualFriends.length - 3}
                            </div>
                          )}
                        </div>
                        <span className="text-[10px] font-bold text-muted-foreground">
                          {mutualFriends.length}{' '}
                          {mutualFriends.length === 1 ? 'Mutual Friend' : 'Mutual Friends'}
                        </span>
                      </button>

                      <Dialog open={isMutualOpen} onOpenChange={setIsMutualOpen}>
                        <DialogContent className="w-[85vw] h-[75vh] max-h-[75vh] md:h-auto md:max-h-[80vh] md:w-[calc(100%-2rem)] md:max-w-md md:rounded-2xl rounded-2xl p-0 overflow-hidden gap-0 flex flex-col border border-border/50 shadow-2xl">
                          <DialogHeader className="p-5 pr-12 border-b border-border shrink-0 flex flex-col justify-center min-h-[64px] bg-background">
                            <DialogTitle className="text-lg font-black tracking-tight text-foreground">
                              Mutual Friends
                            </DialogTitle>
                          </DialogHeader>
                          <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-background">
                            {mutualFriends.map((friend) => {
                              const name =
                                `${friend.relative?.firstName || ''} ${friend.relative?.lastName || ''}`.trim() ||
                                'User';
                              return (
                                <div
                                  key={friend.id}
                                  className="flex items-center gap-3 p-3 rounded-2xl hover:bg-muted/80 active:scale-[0.98] transition-all cursor-pointer border border-transparent hover:border-border/40"
                                  onClick={() => {
                                    setIsMutualOpen(false);
                                    navigate(getProfileRedirectUrl(friend.relativeId));
                                  }}
                                >
                                  <Avatar className="h-10 w-10 shrink-0 ring-2 ring-card shadow-sm">
                                    <AvatarImage
                                      src={friend.relative?.image || undefined}
                                      className="object-cover"
                                    />
                                    <AvatarFallback className="bg-primary/10 text-primary font-bold text-sm">
                                      {name[0] || 'U'}
                                    </AvatarFallback>
                                  </Avatar>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-black text-foreground truncate">
                                      {name}
                                    </p>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </div>

              {/* Bio / description */}
              {type !== 'business' &&
                (business?.description || business?.speciality || userBusiness?.founderStory) && (
                  <div className="px-4 md:px-5 pb-4 md:pb-5">
                    <div className="border-t border-border/40 pt-4">
                      <p className="text-sm text-muted-foreground leading-relaxed font-medium line-clamp-3 md:line-clamp-none">
                        {stripHtmlToPlainText(
                          [business?.description, business?.speciality].filter(Boolean).join(' ')
                        ) || userBusiness?.founderStory}
                      </p>
                    </div>
                  </div>
                )}
            </>
          )}
        </motion.div>
      </div>

      {/* ── SHARE DIALOG ── */}
      {type === 'user' && user ? (
        <Dialog
          open={shareDialogOpen}
          onOpenChange={(open) => {
            setShareDialogOpen(open);
            if (!open) setShareMode('options');
          }}
        >
          <DialogContent className="max-w-md rounded-2xl border-border p-6 shadow-xl">
            <DialogHeader className="text-left sm:text-left">
              <DialogTitle className="text-xl font-bold tracking-tight text-foreground">
                Share contact
              </DialogTitle>
              <p className="text-sm text-muted-foreground">
                Choose how to share {contactName || displayName}&apos;s details.
              </p>
            </DialogHeader>

            <div className="space-y-4">
              {shareMode === 'options' ? (
                <div className="space-y-2">
                  {showShareVCard ? (
                    <button
                      type="button"
                      className="group flex w-full items-center gap-3 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
                      onClick={handleShareVCard}
                    >
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileUser className="h-5 w-5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block font-bold text-foreground">Share vCard</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                          Save as a contact file
                        </span>
                      </span>
                      <ArrowLeft className="h-4 w-4 rotate-180 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="group flex w-full items-center gap-3 rounded-xl border border-border bg-background p-3 text-left transition-colors hover:border-primary/40 hover:bg-primary/[0.04]"
                    onClick={handleShareText}
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Clipboard className="h-5 w-5" />
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block font-bold text-foreground">Share as text</span>
                      <span className="mt-0.5 block text-xs text-muted-foreground">
                        Send in a message or email
                      </span>
                    </span>
                    <ArrowLeft className="h-4 w-4 rotate-180 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="flex items-center gap-1 text-xs font-bold text-muted-foreground transition-colors hover:text-primary"
                    onClick={() => setShareMode('options')}
                  >
                    <ArrowLeft className="h-3.5 w-3.5" />
                    Choose another format
                  </button>
                  <div className="overflow-hidden rounded-xl border border-border bg-muted/30">
                    <div className="flex items-center gap-3 border-b border-border bg-background px-4 py-3">
                      <Avatar className="h-10 w-10 rounded-xl ring-2 ring-primary/10">
                        <AvatarImage src={image} className="object-cover" />
                        <AvatarFallback className="bg-primary/10 font-black text-primary">
                          {initials}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="truncate text-sm font-black text-foreground">
                          {contactName || displayName}
                        </p>
                        <p className="text-[10px] font-bold uppercase tracking-widest text-primary">
                          {shareMode === 'vcard' ? 'Virtual contact card' : 'Contact details'}
                        </p>
                      </div>
                    </div>
                    <pre className="whitespace-pre-wrap px-4 py-3 font-mono text-xs leading-6 text-foreground/80">
                      {shareMode === 'vcard' ? vCard : contactText}
                    </pre>
                  </div>
                  <Button
                    type="button"
                    className="h-10 w-full gap-2 rounded-lg bg-primary font-bold text-primary-foreground shadow-md shadow-primary/20 hover:bg-primary/90"
                    onClick={shareMode === 'vcard' ? handleShareVCard : handleShareText}
                  >
                    <Share2 className="h-4 w-4" />
                    {shareMode === 'vcard' ? 'Share vCard file' : 'Share text details'}
                  </Button>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}
